/**
 * What project managers and members may do on the projects they belong to.
 * Global permissions (e.g. "view all projects") are managed per role above.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PROJECT_CAPABILITIES, type ProjectCapability } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { CheckboxField } from "../../components/ui/controls.js";
import { apiGet, apiPatch } from "../../lib/api.js";
import { ErrorPanel, LoadingPanel, MutationError, Section } from "../shared.js";

type Matrix = { manager: ProjectCapability[]; member: ProjectCapability[] };
const PROJECT_ROLES = ["manager", "member"] as const;

export function ProjectRolesPanel() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [draft, setDraft] = useState<Matrix | null>(null);
  const query = useQuery({
    queryKey: ["project-role-permissions"],
    queryFn: () =>
      apiGet<{ data: Matrix }>("/settings/project-role-permissions").then(
        (response) => response.data,
      ),
  });
  const save = useMutation({
    mutationFn: (matrix: Matrix) =>
      apiPatch("/settings/project-role-permissions", matrix),
    onSuccess: async () => {
      setDraft(null);
      await client.invalidateQueries({
        queryKey: ["project-role-permissions"],
      });
    },
  });
  if (query.isLoading) return <LoadingPanel />;
  if (query.error || !query.data) return <ErrorPanel error={query.error} />;
  const matrix = draft ?? query.data;
  const toggle = (
    role: (typeof PROJECT_ROLES)[number],
    capability: ProjectCapability,
    on: boolean,
  ) =>
    setDraft({
      ...matrix,
      [role]: on
        ? [...matrix[role], capability]
        : matrix[role].filter((item) => item !== capability),
    });
  return (
    <Section
      title={t("projectRoles.title")}
      subtitle={t("projectRoles.subtitle")}
    >
      <MutationError error={save.error} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[28rem] text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-app-muted">
            <tr>
              <th className="py-2 font-medium">
                {t("projectRoles.capability")}
              </th>
              {PROJECT_ROLES.map((role) => (
                <th key={role} className="w-32 py-2 font-medium">
                  {t(`projects.${role}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-app-border">
            {PROJECT_CAPABILITIES.map((capability) => (
              <tr key={capability}>
                <td className="py-2">
                  {t(`projectRoles.capabilities.${capability}`)}
                </td>
                {PROJECT_ROLES.map((role) => (
                  <td key={role} className="py-2">
                    <CheckboxField
                      id={`project-role-${role}-${capability}`}
                      label={
                        <span className="sr-only">
                          {t(`projects.${role}`)} ·{" "}
                          {t(`projectRoles.capabilities.${capability}`)}
                        </span>
                      }
                      checked={matrix[role].includes(capability)}
                      onChange={(on) => toggle(role, capability, on)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button
        className="mt-4"
        disabled={!draft}
        loading={save.isPending}
        onClick={() => draft && save.mutate(draft)}
      >
        {t("common.save")}
      </Button>
    </Section>
  );
}
